/**
 * Board self-service portal — public, long-lived-token authed.
 *
 * Lives at /board?token=<raw>. A board member who holds the link gets
 * their own dashboard of every approval they're a recipient on, split
 * into "needs your decision" and "history". Clicking a pending row deep-
 * links into the existing per-package reviewer portal at /approve/:token.
 */
import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || '';

function fmtDate(d) { return d ? new Date(d).toLocaleString() : '—'; }
function fmtRelative(d) {
  if (!d) return '';
  const ms = new Date(d).getTime() - Date.now();
  const days = Math.round(ms / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'in 1 day';
  if (days > 0)   return `in ${days} days`;
  if (days === -1) return 'yesterday';
  return `${Math.abs(days)} days ago`;
}

const STATUS_CFG = {
  draft:     { label: 'Draft',          color: 'bg-slate-100 text-slate-600 border-slate-200' },
  sent:      { label: 'Awaiting decisions', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  approved:  { label: 'Approved',       color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected:  { label: 'Rejected',       color: 'bg-red-50 text-red-600 border-red-200' },
  expired:   { label: 'Expired',        color: 'bg-slate-100 text-slate-500 border-slate-200' },
  cancelled: { label: 'Cancelled',      color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const DECISION_LABEL = {
  approved:                 'Approved',
  approved_with_conditions: 'Approved with conditions',
  rejected:                 'Rejected',
  abstained:                'Abstained',
  recused:                  'Recused',
};

export default function BoardPortal({ token }) {
  const [stage, setStage] = useState('loading'); // loading | ready | error
  const [identity, setIdentity] = useState(null);
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setError('Missing token.'); setStage('error'); return; }
    let cancelled = false;
    (async () => {
      try {
        const pf = await fetch(`${API}/api/approval-portal/board/${token}/preflight`);
        const pre = await pf.json();
        if (cancelled) return;
        if (!pre.success) {
          setError(pre.message || 'This link is no longer valid.');
          setStage('error'); return;
        }
        setIdentity(pre.preview);
        const r = await fetch(`${API}/api/approval-portal/board/dashboard`, {
          headers: { 'x-board-token': token },
        });
        const d = await r.json();
        if (cancelled) return;
        if (!d.success) {
          setError(d.message || 'Unable to load dashboard.');
          setStage('error'); return;
        }
        setPending(d.pending || []);
        setHistory(d.history || []);
        setStage('ready');
      } catch (e) {
        if (!cancelled) { setError('Unable to reach the approval service.'); setStage('error'); }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-amber-50">
      <header className="bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-white flex items-center justify-center text-sm font-bold">PCG</div>
            <div>
              <div className="text-sm font-bold text-slate-800">Your sign-off requests</div>
              <div className="text-[11px] text-slate-500">SignoffHub · board portal</div>
            </div>
          </div>
          {identity && (
            <div className="text-right">
              <div className="text-sm font-medium text-slate-800">{identity.display_name || identity.email}</div>
              <div className="text-[11px] text-slate-500">{identity.email}</div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {stage === 'loading' && (
          <div className="text-center py-24 text-slate-400 text-sm">Loading your dashboard…</div>
        )}

        {stage === 'error' && (
          <div className="bg-white rounded-2xl border border-red-200 p-8 text-center shadow-sm max-w-lg mx-auto">
            <div className="text-4xl mb-3">🚫</div>
            <h2 className="text-lg font-bold text-slate-800">We can't open your dashboard</h2>
            <p className="text-sm text-slate-500 mt-2">{error}</p>
            <p className="text-xs text-slate-400 mt-4">If this is the most recent link your compliance office sent and it still doesn't work, please reach out to them to issue a fresh one.</p>
          </div>
        )}

        {stage === 'ready' && (
          <>
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Needs your decision</h2>
              <p className="text-sm text-slate-500 mt-1">
                {pending.length === 0
                  ? 'You\'re all caught up. Nothing waiting on you.'
                  : `${pending.length} ${pending.length === 1 ? 'package is' : 'packages are'} waiting for your decision.`}
              </p>
              {pending.length > 0 && (
                <div className="grid gap-3 mt-4">
                  {pending.map((p) => (
                    <PendingCard key={p.recipient_id} pkg={p} />
                  ))}
                </div>
              )}
            </section>

            <section>
              <h2 className="text-lg font-bold text-slate-800 tracking-tight">History</h2>
              <p className="text-sm text-slate-500 mt-1">Every approval package you've been on, with your decision on the record.</p>
              {history.length === 0 ? (
                <div className="text-center py-8 text-xs text-slate-400 mt-4">No history yet.</div>
              ) : (
                <ul className="bg-white border border-slate-200 rounded-2xl mt-4 divide-y divide-slate-100">
                  {history.map((p) => (
                    <HistoryRow key={p.recipient_id} pkg={p} />
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </main>

      <footer className="text-center text-[11px] text-slate-400 py-6">
        Precision Compliance Group · {new Date().getFullYear()}
      </footer>
    </div>
  );
}

function PendingCard({ pkg }) {
  const cfg = STATUS_CFG[pkg.package_status] || STATUS_CFG.sent;
  const deadlinePast = pkg.deadline_at && new Date(pkg.deadline_at) < new Date();
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-bold text-slate-900">{pkg.package_name}</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase tracking-wider ${cfg.color}`}>{cfg.label}</span>
          </div>
          {pkg.package_description && (
            <p className="text-sm text-slate-600 mt-1">{pkg.package_description}</p>
          )}
          <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-500 flex-wrap">
            <span>📑 {pkg.item_count} {pkg.item_count === 1 ? 'item' : 'items'}</span>
            <span className={deadlinePast ? 'text-red-600 font-semibold' : ''}>
              ⏰ Due {fmtRelative(pkg.deadline_at)}
            </span>
            <span className="text-slate-400">Invited {fmtDate(pkg.invited_at)}</span>
          </div>
        </div>
      </div>
      <div className="mt-4 text-xs text-slate-500">
        To open the secure review portal for this package, please use the most recent email invitation we sent you. Your compliance office can re-send a fresh link any time if you've lost it.
      </div>
    </div>
  );
}

function HistoryRow({ pkg }) {
  const cfg = STATUS_CFG[pkg.package_status] || STATUS_CFG.sent;
  return (
    <li className="px-5 py-3 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-900 truncate">{pkg.package_name}</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase tracking-wider ${cfg.color}`}>{cfg.label}</span>
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          {pkg.decision
            ? <>You {DECISION_LABEL[pkg.decision]?.toLowerCase() || pkg.decision} on {fmtDate(pkg.decision_at)}</>
            : <>Package closed without your decision</>}
        </div>
        {pkg.decision_comment && (
          <div className="text-[11px] italic text-slate-600 mt-1">"{pkg.decision_comment}"</div>
        )}
      </div>
    </li>
  );
}
